import { useEffect, useState } from 'react';
import { onUnreadCount, unreadCount } from './unread';

/**
 * The number on the Jarvis icon.
 *
 * Gordon: "a small circle with the number of notifications/messages received
 * etc on the icon of the desktop app so that the user knows that he has unread
 * messages. Needs to be clear and simple to read."
 *
 * ONE NUMBER, painted in three places, because people reach Jarvis three ways:
 *
 *   THE INSTALLED APP'S ICON - the Badging API, navigator.setAppBadge(n). In
 *   Edge and Chrome on Windows that is a small circle with the number over
 *   the app's button on the taskbar; on a Mac it is the red Dock badge.
 *   Chromium shows anything over 99 as "99+" itself, so it is given the real
 *   number. Elsewhere (Firefox, a browser without the API) it is simply not
 *   there, and nothing is called.
 *
 *   THE BROWSER TAB'S ICON - for anybody using Jarvis in a tab rather than
 *   the app: the favicon redrawn on a canvas with a red circle and white bold
 *   digits in its corner, cut out from the logo so it reads at 16 pixels.
 *
 *   THE TAB'S TITLE - "(3) Jarvis", which is also what the Windows taskbar
 *   and Alt+Tab show for a browser window.
 *
 * WHAT IS COUNTED. Two numbers the bell's one poll already brings back
 * (GET /api/notifications, every minute and whenever something changes):
 *
 *   messages  `chat_unread` - every unread MESSAGE: direct conversations and
 *             their topics, ticket chats and groups. lib/unread holds it; the
 *             Chat menu item shows the same number.
 *   notices   the bell's rows that are NOT chat rows - tickets to approve or
 *             assign, hours to sign off, to-do reminders and activity,
 *             meeting invitations, a recording that is ready. The bell lists
 *             unread chat as rows of its own ("4 unread from Marco"); those
 *             are left out here because the messages behind them are already
 *             in `messages`, and counting both would count Marco twice.
 *
 * So the badge is "messages waiting + other things waiting", and it matches
 * the Chat badge plus the bell's number less its chat rows.
 *
 * WHAT A PERSON CAN CHOOSE (the account menu, "Count on the Jarvis icon"):
 * messages only (the default - Gordon, 24 October: "Count only unread
 * messages only."), everything, or off. Kept in this browser's localStorage,
 * because it is a choice about THIS device - the laptop that badges and the
 * phone that does not are both reasonable at once - and it needs no
 * migration. Nothing stored means the default; a choice somebody made is
 * kept, so whoever picked Everything still has it. Off clears all three
 * places.
 *
 * WHEN IT MOVES. Whenever the bell's poll answers - every minute while Jarvis
 * is open, minimised or behind other windows included; at once when the
 * window comes back into view; and at once when a conversation is read or a
 * message arrives on the chat screen (lib/chatLink announces both). It is
 * cleared at sign-out.
 *
 * NOT WHILE JARVIS IS CLOSED. The badge is set by the page, so with no Jarvis
 * window open nothing is counting. That needs web push, which is not built:
 * public/sw.js already has the hook a push message would call
 * (setBadgeFromPush), so the day push exists the server sends the number and
 * the badge moves with the app closed.
 */

export const BADGE_PREF_KEY = 'jarvis.iconBadge';
export const BADGE_PREFS = ['all', 'messages', 'off'];
const DEFAULT_PREF = 'messages';

/* ------------------------------------------------------------ inputs --- */

let notices = 0;
let pref = readPref();
let known = false;          // nothing is painted until the first poll answers
const prefListeners = new Set();

function readPref() {
  try {
    const v = window.localStorage.getItem(BADGE_PREF_KEY);
    return BADGE_PREFS.includes(v) ? v : DEFAULT_PREF;
  } catch {
    return DEFAULT_PREF;
  }
}

/*
 * Which feature each kind of notice belongs to (078, Settings -> Features).
 * The server already leaves a switched-off feature's notices out of the bell
 * (routes/notifications.js, `gated`), so this is belt and braces: a bell
 * answer from a moment before a switch moved must not keep counting it.
 */
const FEATURE_OF_KIND = {
  todo_reminder: 'todos', todo_assigned: 'todos', todo_comment: 'todos',
  todo_shared: 'todos', todo_ready: 'todos',
  recording_ready: 'recording',
  meeting_invited: 'meetings', meeting_starting: 'meetings', minutes_ready: 'meetings'
};
const kindOf = (n) => (n && (n.kind || n.type)) || '';

/**
 * How many of the bell's rows are notices rather than messages.
 * `isOn(feature)` answers whether this person may use a feature.
 */
export function noticeCountOf(list, isOn = () => true) {
  if (!Array.isArray(list)) return 0;
  return list.filter((n) => {
    const kind = kindOf(n);
    if (!n || kind === 'chat') return false;
    const feature = FEATURE_OF_KIND[kind] || (/^todo_/.test(kind) ? 'todos' : null);
    return !feature || isOn(feature);
  }).length;
}

/** The bell tells this what it found; the messages come from lib/unread. */
export function setNoticeCount(n) {
  const next = Number.isFinite(Number(n)) ? Math.max(0, Math.floor(Number(n))) : 0;
  known = true;
  if (next === notices) { paint(); return; }
  notices = next;
  paint();
}

/**
 * The number, as the person's preference has it - THE ONE PLACE the total is
 * worked out. Anything new that should count goes in here as another term:
 * unread replies in chat Branches, for one, are a message count, so if the
 * server folds them into `chat_unread` they arrive by themselves, and if it
 * sends them separately they are one more addend beside unreadCount().
 */
export function badgeTotal() {
  if (pref === 'off') return 0;
  return unreadCount() + (pref === 'all' ? notices : 0);
}

/** Signed out: nothing on any icon, and nothing remembered for the next person. */
export function resetBadge() {
  notices = 0;
  known = true;
  paint(0);
  known = false;
}

/* ------------------------------------------------------- preference --- */

export const badgePref = () => pref;

export function setBadgePref(next) {
  if (!BADGE_PREFS.includes(next)) return;
  pref = next;
  try { window.localStorage.setItem(BADGE_PREF_KEY, next); } catch { /* private window */ }
  prefListeners.forEach((fn) => { try { fn(pref); } catch { /* ignore */ } });
  paint();
}

export function useBadgePref() {
  const [value, setValue] = useState(pref);
  useEffect(() => {
    prefListeners.add(setValue);
    return () => { prefListeners.delete(setValue); };
  }, []);
  return [value, setBadgePref];
}

/* ------------------------------------------------------------ paint --- */

let painted = null;         // the last number painted, so nothing is redone

/** "99+" past 99, as Chromium does on the app icon. */
export const badgeLabel = (n) => (n > 99 ? '99+' : String(n));

function paint(force) {
  if (typeof window === 'undefined') return;
  const n = force === 0 ? 0 : badgeTotal();
  if (!known && force !== 0) return;
  if (n === painted) return;
  painted = n;
  appIcon(n);
  tabTitle(n);
  tabIcon(n);
}

/* The installed app's icon. Feature-detected, and a refusal is swallowed:
   Chromium rejects the call from a page that is not an installed app's on
   some platforms, and a badge is never worth an error. */
function appIcon(n) {
  const nav = window.navigator;
  try {
    if (n > 0) {
      if (typeof nav.setAppBadge === 'function') {
        const p = nav.setAppBadge(n);
        if (p && p.catch) p.catch(() => {});
      }
    } else if (typeof nav.clearAppBadge === 'function') {
      const p = nav.clearAppBadge();
      if (p && p.catch) p.catch(() => {});
    } else if (typeof nav.setAppBadge === 'function') {
      const p = nav.setAppBadge(0);
      if (p && p.catch) p.catch(() => {});
    }
  } catch { /* not supported here */ }
}

/* ------------------------------------------------------------ title --- */

/*
 * "(3) Jarvis". The chat screen also writes the title - it flashes "New
 * message from Marco" while the tab is not looked at, and puts back what it
 * found afterwards (lib/chime) - so rather than fight it, the prefix is
 * re-applied whenever anything changes the title: whatever is there, with
 * any old "(n) " taken off, gets the current one put on.
 */
const PREFIX = /^\(\d+\+?\)\s/;
let titleObserver = null;

function wantedTitle(current, n) {
  const base = String(current || '').replace(PREFIX, '');
  return n > 0 ? `(${badgeLabel(n)}) ${base}` : base;
}

function tabTitle(n) {
  if (typeof document === 'undefined') return;
  const want = wantedTitle(document.title, n);
  if (document.title !== want) document.title = want;
  if (!titleObserver && typeof MutationObserver === 'function') {
    const head = document.querySelector('head');
    if (!head) return;
    titleObserver = new MutationObserver(() => {
      const now = painted || 0;
      const again = wantedTitle(document.title, now);
      if (document.title !== again) document.title = again;
    });
    titleObserver.observe(head, { subtree: true, childList: true, characterData: true });
  }
}

/* -------------------------------------------------------- tab icon --- */

/*
 * The favicon with the count on it.
 *
 * Drawn at 64 pixels and left to the browser to scale, which it does well
 * from a multiple of 16. The circle is big - most of the icon's right half -
 * because at 16 pixels on the tab strip anything smaller is a red speck:
 * one digit sits in a circle two thirds of the icon wide, two digits and
 * "99+" in a pill as wide as the icon. The logo is cut away in a ring round
 * it, so the red never runs into the navy and the edge stays crisp in a light
 * tab strip and a dark one alike. White bold digits on #D1242F: 5:1.
 */
export const BADGE_RED = '#D1242F';
const SIZE = 64;
let baseIcon = null;        // { href, type } of the page's own favicon
let baseImage = null;       // its pixels, once loaded
let drawSeq = 0;

function iconLink() {
  if (typeof document === 'undefined') return null;
  let link = document.querySelector('link[rel~="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (!baseIcon) {
    baseIcon = { href: link.getAttribute('href') || '/favicon.svg', type: link.getAttribute('type') || '' };
  }
  return link;
}

function loadBase() {
  if (baseImage) return Promise.resolve(baseImage);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => { baseImage = img; resolve(img); };
    img.onerror = () => resolve(null);    // a red circle on nothing beats no badge
    img.src = baseIcon.href;
  });
}

/** Draws the badged icon and answers its PNG data URL (or null without canvas). */
export function drawBadgedIcon(n, img) {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext && canvas.getContext('2d');
  if (!ctx) return null;
  if (img) {
    try { ctx.drawImage(img, 0, 0, SIZE, SIZE); } catch { /* tainted or broken: draw on nothing */ }
  }
  const label = badgeLabel(n);
  const h = label.length === 1 ? 42 : 36;            // the badge's height
  const w = label.length === 1 ? h : SIZE;             // round for one digit, a pill for more
  const x = SIZE - w;
  const y = 0;
  const r = h / 2;
  const pill = (c, grow) => {
    c.beginPath();
    const left = x - grow; const top = y - grow; const width = w + 2 * grow; const height = h + 2 * grow;
    const rr = r + grow;
    c.moveTo(left + rr, top);
    c.lineTo(left + width - rr, top);
    c.arc(left + width - rr, top + rr, rr, -Math.PI / 2, Math.PI / 2);
    c.lineTo(left + rr, top + height);
    c.arc(left + rr, top + rr, rr, Math.PI / 2, (3 * Math.PI) / 2);
    c.closePath();
  };
  // The ring cut out of the logo.
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  pill(ctx, 5);
  ctx.fill();
  ctx.restore();
  // The badge.
  pill(ctx, 0);
  ctx.fillStyle = BADGE_RED;
  ctx.fill();
  // The digits, as large as the badge allows.
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  let px = label.length === 1 ? 34 : label.length === 2 ? 31 : 27;
  const fit = w - 8;
  do {
    ctx.font = `800 ${px}px "Segoe UI", system-ui, -apple-system, Arial, sans-serif`;
    if (ctx.measureText(label).width <= fit) break;
    px -= 1;
  } while (px > 14);
  ctx.fillText(label, x + w / 2, y + h / 2 + 1.5);
  try { return canvas.toDataURL('image/png'); } catch { return null; }
}

function tabIcon(n) {
  const link = iconLink();
  if (!link) return;
  const seq = ++drawSeq;
  if (n <= 0) {
    link.setAttribute('href', baseIcon.href);
    if (baseIcon.type) link.setAttribute('type', baseIcon.type); else link.removeAttribute('type');
    link.removeAttribute('data-badge');
    return;
  }
  loadBase().then((img) => {
    if (seq !== drawSeq) return;     // a newer number got there first
    const url = drawBadgedIcon(n, img);
    if (!url) return;
    link.setAttribute('type', 'image/png');
    link.setAttribute('href', url);
    link.setAttribute('data-badge', badgeLabel(n));
  });
}

/* --------------------------------------------------- wiring, once --- */

// A change in the message count repaints; nothing else needs to listen.
onUnreadCount(() => paint());
